import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export interface BrandConfig {
  kind: "brand" | "platform";
  slug?: string;
  name: string;
  theme: {
    brand?: string;
    brandForeground?: string;
    accent?: string;
    logoText?: string;
    radius?: string;
    logoUrl?: string | null;
    logoLightUrl?: string | null;
    markUrl?: string | null;
    faviconUrl?: string | null;
    sidebar?: "dark" | "light" | null;
  };
  support_email?: string;
  signup_enabled?: boolean;
}

const BrandCtx = React.createContext<BrandConfig | null>(null);

/** Tema runtime por domínio: o mesmo build atende todas as marcas (sem rebuild por cliente). */
export function BrandProvider({ children }: { children: React.ReactNode }) {
  const q = useQuery({ queryKey: ["public-brand", location.host], queryFn: () => api.get<BrandConfig>("/api/public/brand"), staleTime: 5 * 60_000, retry: 1 });
  React.useEffect(() => {
    if (!q.data) return;
    const root = document.documentElement;
    const t = q.data.theme ?? {};
    const safe = (v?: string) => (v && /^#[0-9a-fA-F]{3,8}$/.test(v) ? v : undefined);
    if (safe(t.brand)) root.style.setProperty("--cm-brand", t.brand!);
    if (safe(t.brandForeground)) root.style.setProperty("--cm-brand-foreground", t.brandForeground!);
    if (safe(t.accent)) root.style.setProperty("--cm-accent", t.accent!);
    if (t.radius && /^\d+(\.\d+)?rem$/.test(t.radius)) root.style.setProperty("--cm-radius", t.radius);
    if (safe(t.brand)) root.style.setProperty("--cm-focus", t.brand!);
    root.dataset.sidebar = t.sidebar === "light" ? "light" : "dark";
    document.title = q.data.name;
    setFavicon(t.faviconUrl ?? t.markUrl ?? null, (t.logoText || q.data.name || "?").slice(0, 1).toUpperCase(), safe(t.brand) ?? "#0f172a");
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = safe(t.brand) ?? "#0f172a";
  }, [q.data]);
  if (q.isLoading) return <div className="flex h-full items-center justify-center text-sm text-muted">Carregando…</div>;
  if (q.isError || !q.data || !q.data.name)
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div>
          <p className="text-lg font-semibold">Domínio não configurado</p>
          <p className="mt-1 text-sm text-muted">Este endereço não está associado a nenhuma marca verificada.</p>
        </div>
      </div>
    );
  return <BrandCtx.Provider value={q.data}>{children}</BrandCtx.Provider>;
}

const ASSET_RE = /^(\/brand\/[A-Za-z0-9_./-]+|https:\/\/[^\s"'<>]+)$/;
export function safeAssetUrl(u?: string | null): string | null {
  return u && ASSET_RE.test(u) ? u : null;
}

/** Favicon por marca: arquivo configurado ou ícone SVG com a inicial e a cor da marca (sem vazar outra marca). */
function setFavicon(url: string | null, letter: string, color: string) {
  const safe = safeAssetUrl(url);
  const href =
    safe ??
    `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="${color}"/><text x="16" y="22" font-family="Arial,sans-serif" font-size="18" font-weight="700" text-anchor="middle" fill="#fff">${letter.replace(/[^A-Za-z0-9]/g, "")}</text></svg>`,
    )}`;
  for (const rel of ["icon", "apple-touch-icon"]) {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = rel === "apple-touch-icon" && safe?.startsWith("/brand/clickmetrics") ? "/brand/apple-touch-icon.png" : href;
  }
}

export function useBrand(): BrandConfig {
  const b = React.useContext(BrandCtx);
  if (!b) throw new Error("useBrand fora do BrandProvider");
  return b;
}
